import React from "react"
import Select, { GetOptionLabel, GetOptionValue, components, OptionProps } from "react-select"
import CreatableSelect from "react-select/creatable"

export interface Option {
  value: string
  label: string
}

interface Props {
  options: Option[]
  value: Option[]
  onChange: (val: Option[]) => void
  placeholder?: string
  isDisabled?: boolean
  getOptionLabel?: GetOptionLabel<any>
  getOptionValue?: GetOptionValue<any>
  // When provided, the field becomes type-to-create: typing a new value and
  // choosing "Add …" calls this with the typed text (e.g. to create a section).
  onCreateOption?: (inputValue: string) => void
  // When provided, each option in the menu shows a trash icon to delete it.
  onDeleteOption?: (option: Option) => void
  isLoading?: boolean
}

const MultiSelectSection = ({
  options,
  value,
  onChange,
  placeholder = "Select options",
  isDisabled = false,
  onCreateOption,
  onDeleteOption,
  isLoading = false,
}: Props) => {
  // Option row with an optional inline delete button.
  const OptionWithDelete = (props: OptionProps<Option, true>) => (
    <components.Option {...props}>
      <div className="d-flex align-items-center justify-content-between">
        <span>{props.data.label}</span>
        {onDeleteOption && !(props.data as any).__isNew__ && (
          <i
            className="ti ti-trash text-danger ms-2"
            title="Delete section"
            style={{ cursor: "pointer" }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDeleteOption(props.data)
            }}
          />
        )}
      </div>
    </components.Option>
  )

  const common: any = {
    isMulti: true as const,
    isDisabled,
    options,
    value,
    onChange: (val: any) => onChange(val as Option[]),
    placeholder,
    closeMenuOnSelect: false,
    hideSelectedOptions: false,
    classNamePrefix: "react-select",
    components: onDeleteOption ? { Option: OptionWithDelete } : undefined,
    // Render the menu in a body portal so it floats above the dialog instead of
    // overlapping the fields below it (and isn't clipped by the modal's overflow).
    menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
    menuPosition: "fixed" as const,
    // Must beat .modal's z-index (10105 — see _header.scss) or a portaled
    // menu opened inside any modal renders invisibly behind it.
    styles: {
      menuPortal: (base: any) => ({ ...base, zIndex: 10110 }),
    },
  }

  if (onCreateOption) {
    return (
      <CreatableSelect
        {...common}
        isLoading={isLoading}
        onCreateOption={onCreateOption}
        formatCreateLabel={(input: string) => `Add section "${input}"`}
      />
    )
  }

  return <Select {...common} />
}

export default MultiSelectSection
